<script setup>
/* eslint-disable */
import { reactive, ref, onMounted, watch, computed } from 'vue';
import { useStore } from 'vuex';
import api from "@/services/api.js";
import ArgonInput from '@/components/ArgonInput.vue';
import SelectorJerarquicoPersonal from '@/components/SelectorJerarquicoPersonal.vue';
import logo from "@/assets/img/fondotam-blanco.png";
import Swal from "sweetalert2";
import { validarRequeridos } from '@/utils/validators.js';
import { construirUrlImagen } from '@/utils/formatters.js';

// --- ESTADOS ---
const activoEncontrado = ref(null);
const codigoBusqueda = ref('');
const store = useStore();
const cargando = ref(false);
const subiendoRespaldo = ref(false);
const usuarioSeleccionadoObj = ref(null);

const formulario = reactive({
  activo: '',
  usuario: '',
  regional: '',
  gerencia: '',
  seccion: '',
  cargo: '',
  ubicacion: '',
  tipo: 'Permanente',
  esta_activa: true,
  num_acta: '',
  fecha_fin_estimada: '',
  observaciones: ''
});

const ubicaciones = computed(() => store.state.catalogo.ubicaciones);

// --- CARGA INICIAL ---
onMounted(async () => {
  try {
    cargando.value = true;
    await store.dispatch('catalogo/fetchUbicaciones');

  } catch (e) {
    console.error("Error de carga:", e);
  } finally { cargando.value = false; }
});

// --- BÚSQUEDA DE ACTIVO DISPONIBLE ---
const buscarActivo = async () => {
  if (!codigoBusqueda.value) return;
  cargando.value = true;
  activoEncontrado.value = null;

  try {
    const query = codigoBusqueda.value.trim();
    const { data } = await api.get(`activos/?search=${query}`);

    if (data && data.length > 0) {
      const activo = data[0];
      if (activo.situacion_activo !== 'Disponible') {
        Swal.fire("No Disponible", `El activo ya está ${activo.situacion_activo}. Para cambiar de dueño use Transferencias.`, "warning");
      } else {
        activoEncontrado.value = activo;
        formulario.activo = activo.id;
        Swal.fire({ icon: 'success', title: 'Activo Encontrado', text: activo.nombre_especifico, timer: 1500, showConfirmButton: false });
      }
    } else {
      Swal.fire("No Encontrado", "No existe un activo con ese código.", "error");
    }
  } catch (e) {
    console.error("Error en búsqueda:", e);
  } finally { cargando.value = false; }
};

const handleSubirRespaldoGeneral = async (e) => {
  const file = e.target.files[0];
  if (!file) return;
  if (!formulario.usuario) {
    return Swal.fire("Atención", "Seleccione primero a un funcionario.", "warning");
  }
  const fd = new FormData();
  fd.append('documento_respaldo_general', file);
  try {
    subiendoRespaldo.value = true;
    const { data } = await api.patch(`users/${formulario.usuario}/`, fd);
    if (usuarioSeleccionadoObj.value) {
      usuarioSeleccionadoObj.value.documento_respaldo_general = data.documento_respaldo_general;
    }
    Swal.fire("Respaldo Actualizado", "El documento de respaldo general del usuario fue actualizado correctamente.", "success");
  } catch (err) {
    console.error("Error al subir respaldo general:", err);
    Swal.fire("Error", "No se pudo actualizar el documento de respaldo general.", "error");
  } finally {
    subiendoRespaldo.value = false;
  }
};

// --- GUARDAR DESIGNACIÓN ---
const guardarAsignacion = async () => {
  const camposRequeridos = {
    num_acta: formulario.num_acta,
    usuario: formulario.usuario,
    ubicacion: formulario.ubicacion
  };
  if (!validarRequeridos(camposRequeridos, "Debe asignar un Responsable, una Ubicación física y el número de acta.")) return;

  cargando.value = true;
  const fd = new FormData();

  Object.keys(formulario).forEach(key => {
    if (formulario[key] !== null && formulario[key] !== '') {
      fd.append(key, formulario[key]);
    }
  });

  try {
    await api.post('designaciones/', fd);
    Swal.fire({
      title: "Asignación Registrada",
      text: "El activo ahora tiene un custodio legal asignado.",
      icon: "success",
      confirmButtonText: "Aceptar",
      confirmButtonColor: "#2dce89"
    });
    resetForm();
  } catch (error) {
    console.error("Error guardando asignación:", error);
  } finally { cargando.value = false; }
};

const resetForm = () => {
  activoEncontrado.value = null;
  codigoBusqueda.value = '';
  usuarioSeleccionadoObj.value = null;
  Object.assign(formulario, {
    activo: '', usuario: '', regional: '', gerencia: '', seccion: '', cargo: '',
    ubicacion: '', tipo: 'Permanente', esta_activa: true, fecha_fin_estimada: '',
    observaciones: ''
  });
};
</script>
<template>
  <div class="container-fluid py-3 py-md-4 min-vh-100">
    <div class="row justify-content-center">
      <div class="col-lg-10">
        <div class="card card-custom border-0 shadow-lg overflow-hidden mb-4 border-radius-2xl">
          
          <div class="header-tamep d-flex flex-column flex-sm-row align-items-center px-3 px-md-4 py-3 text-white text-center text-sm-start gap-2 gap-sm-3">
            <div class="logo-container-left shadow-sm">
              <img :src="logo" class="logo-img">
            </div>
            <div>
              <h6 class="mb-0 text-white text-uppercase font-weight-bolder">Asignación de Activos (Custodia)</h6>
              <small class="opacity-7 text-xs d-block mt-1 mt-sm-0">Registro oficial de entrega de bienes a funcionarios</small>
            </div>
          </div>

          <div class="card-body p-3 p-md-4">
            <div class="row">

              <div
                class="col-lg-5 border-lg-end border-custom-divider pb-4 pb-lg-0 border-bottom border-lg-bottom-0 mb-4 mb-lg-0">
                <div class="d-flex align-items-center mb-3">
                  <span class="badge-step me-2">1</span>
                  <h6 class="mb-0 font-weight-bold text-uppercase text-title-custom">Seleccionar Activo</h6>
                </div>

                <div class="row g-2 mb-4">
                  <div class="col-9 col-md-8">
                    <input v-model="codigoBusqueda" class="form-control border-2 input-custom w-100"
                      placeholder="Código TAMEP o TAB..." @keyup.enter="buscarActivo">
                  </div>
                  <div class="col-3 col-md-4">
                    <button @click="buscarActivo" class="btn btn-tamep-dark w-100 mb-0 px-2" :disabled="cargando">
                      <i class="fas fa-search me-1 d-none d-sm-inline"></i>
                      <i class="fas fa-search d-inline d-sm-none"></i>
                    </button>
                  </div>
                </div>

                <div v-if="activoEncontrado"
                  class="asset-preview p-3 border-radius-lg animate__animated animate__fadeIn preview-bg-custom">
                  <img :src="construirUrlImagen(activoEncontrado.imagen_activo)"
                    class="w-100 border-radius-lg shadow mb-3 img-fluid main-asset-img">
                  <h6 class="mb-1 text-title-custom text-wrap">{{ activoEncontrado.nombre_especifico }}</h6>
                  <p class="text-xs mb-2 text-custom-muted">Serie: {{ activoEncontrado.serie }}</p>
                  <div class="d-flex justify-content-between border-top pt-2 border-custom-divider">
                    <small class="font-weight-bold text-custom">Código:</small>
                    <small class="text-custom-muted">{{ activoEncontrado.codigo_tamep }}</small>
                  </div>
                </div>
                <div v-else class="text-center py-4 py-md-5 opacity-4 text-custom">
                  <i class="fas fa-barcode fa-3x fa-md-4x mb-3"></i>
                  <p class="text-sm text-md-base px-3">Escanee o ingrese un código para comenzar</p>
                </div>
              </div>

              <div class="col-lg-7 ps-lg-4">
                <div v-if="activoEncontrado" class="animate__animated animate__fadeIn">
                  <div class="d-flex align-items-center mb-3">
                    <span class="badge-step me-2">2</span>
                    <h6 class="mb-0 font-weight-bold text-uppercase text-title-custom">Datos del Custodio</h6>
                  </div>

                  <div class="row g-3">
                    <SelectorJerarquicoPersonal
                      v-model:regional="formulario.regional"
                      v-model:gerencia="formulario.gerencia"
                      v-model:seccion="formulario.seccion"
                      v-model:cargo="formulario.cargo"
                      v-model:usuario="formulario.usuario"
                      v-model:usuarioObj="usuarioSeleccionadoObj"
                    />

                    <!-- SECCIÓN DE RESPALDO GENERAL DEL USUARIO -->
                    <div v-if="formulario.usuario" class="col-12 mt-2">
                      <div class="card bg-light border p-3 border-radius-lg">
                        <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
                          <div>
                            <h6 class="mb-0 text-sm font-weight-bold text-dark">
                              <i class="fas fa-file-pdf me-2 text-danger"></i>Respaldo General de Custodia del Funcionario
                            </h6>
                            <small class="text-xs text-secondary d-block mt-1">
                              Documento general de asignación institucional cargado por el Administrador.
                            </small>
                          </div>

                          <div v-if="usuarioSeleccionadoObj?.documento_respaldo_general">
                            <a :href="usuarioSeleccionadoObj.documento_respaldo_general" target="_blank" rel="noopener noreferrer" class="btn btn-sm btn-outline-danger mb-0 me-2">
                              <i class="fas fa-external-link-alt me-1"></i> Ver Respaldo (PDF)
                            </a>
                          </div>
                          <div v-else>
                            <span class="badge bg-gradient-warning text-xxs">Sin Respaldo Cargado</span>
                          </div>
                        </div>

                        <!-- Cargar o actualizar respaldo general del usuario -->
                        <div class="mt-3 pt-2 border-top">
                          <label class="label-custom text-xs font-weight-bold">
                            {{ usuarioSeleccionadoObj?.documento_respaldo_general ? 'Modificar/Actualizar Respaldo General (PDF):' : 'Subir Respaldo General (PDF):' }}
                          </label>
                          <input type="file" @change="handleSubirRespaldoGeneral" accept=".pdf" class="form-control form-control-sm input-custom" :disabled="subiendoRespaldo">
                        </div>
                      </div>
                    </div>

                    <div class="col-12">
                      <hr class="horizontal border-custom-divider my-2">
                    </div>

                    <div class="col-12 mb-2">
                      <div class="d-flex align-items-center">
                        <span class="badge-step me-2">3</span>
                        <h6 class="mb-0 font-weight-bold text-uppercase text-title-custom">Detalles de Entrega</h6>
                      </div>
                    </div>

                    <div class="col-12">
                      <label class="label-custom text-primary">Número de Acta de Designación (Manual)</label>
                      <div class="input-group">
                        <span class="input-group-text bg-input-addon border-2"><i
                            class="fas fa-file-signature text-custom"></i></span>
                        <input v-model="formulario.num_acta" type="text"
                          class="form-control border-2 ps-2 font-weight-bold input-custom"
                          placeholder="Ej: DES-2026-0001" style="text-transform: uppercase;">
                      </div>
                      <small class="text-xxs text-custom-muted d-block mt-1">Correlativo interno del acta de entrega.</small>
                    </div>

                    <div class="col-12 col-md-6">
                      <label class="label-custom">Ubicación Física (Sala)</label>
                      <select v-model="formulario.ubicacion" class="form-select-tamep select-custom">
                        <option v-for="u in ubicaciones" :key="u.id" :value="u.id">{{ u.nombre_sala }}</option>
                      </select>
                    </div>
                    <div class="col-12 col-md-6">
                      <label class="label-custom">Tipo de Asignación</label>
                      <select v-model="formulario.tipo" class="form-select-tamep select-custom">
                        <option value="Permanente">Asignación Permanente (Cargo)</option>
                        <option value="Temporal">Préstamo Temporal</option>
                      </select>
                    </div>
                    <div class="col-12 col-md-6" v-if="formulario.tipo === 'Temporal'">
                      <label class="label-custom">Fecha de Devolución Estimada</label>
                      <input type="date" v-model="formulario.fecha_fin_estimada"
                        class="form-control-tamep input-custom">
                    </div>
                    <div class="col-12">
                      <label class="label-custom">Observaciones de Entrega</label>
                      <textarea v-model="formulario.observaciones" class="form-control border-2 input-custom"
                        rows="2"></textarea>
                    </div>
                  </div>

                  <button @click="guardarAsignacion" class="btn btn-tamep-primary w-100 mt-4 shadow-lg py-3 text-wrap"
                    :disabled="cargando">
                    <i class="fas fa-save me-2"></i> {{ cargando ? 'REGISTRANDO...' : 'FINALIZAR ASIGNACIÓN' }}
                  </button>
                </div>

                <div v-else
                  class="h-100 d-flex align-items-center justify-content-center text-center opacity-5 text-custom py-5 py-lg-0">
                  <div class="px-3">
                    <i class="fas fa-user-tag fa-3x fa-md-4x mb-3"></i>
                    <p class="text-sm text-md-base">Complete el paso 1 para habilitar la asignación</p>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>